import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { doc, collection, query, where, onSnapshot } from 'firebase/firestore';
import LogoDisplay from './LogoDisplay';
import StudyStayLogo from './StudyStayLogo';
// getUnreadNotifications no longer needed — using real-time listener below
import { db } from '../firebase/config';
import { useUnreadCount, useAgencyUnreadCount } from '../hooks/useMessages';
import useLandlordBadges from '../hooks/useLandlordBadges';
import { countTenantNotificationBuckets, mergeNotificationDocs } from '../utils/tenantNotificationBuckets';
import { 
  Home, 
  Star, 
  MessageCircle, 
  User, 
  FileText, 
  Calendar, 
  CalendarDays,
  Eye,
  LogOut,
  Menu,
  X,
  Building,
  Key,
  ChevronDown,
  Bell
} from 'lucide-react';

export default function WebsiteHeader() {
  const { user, userType, landlordType, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [tenantNotificationCounts, setTenantNotificationCounts] = useState({ contracts: 0, viewings: 0, mijnPand: 0 });
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const { unreadCount: unreadMessageCount } = useUnreadCount(user?.uid);
  const { unreadCount: unreadAgencyMessageCount } = useAgencyUnreadCount(user?.uid);
  const landlordBadges = useLandlordBadges(
    userType === 'landlord' && landlordType !== 'agency' ? user?.uid : null
  );
  const [viewingsCountA, setViewingsCountA] = useState(0);
  const [viewingsCountB, setViewingsCountB] = useState(0);
  const pendingViewingsCount = (userType === 'landlord' && landlordType !== 'agency')
    ? landlordBadges.pendingViewingsCount
    : (viewingsCountA + viewingsCountB);
  const pendingContractsCount = (userType === 'landlord' && landlordType !== 'agency')
    ? landlordBadges.pendingContractsCount
    : 0;
  const [tenantPendingContractsCount, setTenantPendingContractsCount] = useState(0);
  const [isBellOpen, setIsBellOpen] = useState(false);
  const [allLandlordNotifs, setAllLandlordNotifs] = useState([]);
  const [bellSeenAt, setBellSeenAt] = useState(() => Date.now());
  const [bellHighlightBefore, setBellHighlightBefore] = useState(0);
  const bellRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!user?.uid || userType !== 'tenant') {
      setTenantNotificationCounts({ contracts: 0, viewings: 0, mijnPand: 0 });
      return;
    }

    const recipientQuery = query(
      collection(db, 'notifications'),
      where('recipientUserId', '==', user.uid),
      where('read', '==', false)
    );
    const tenantQuery = query(
      collection(db, 'notifications'),
      where('tenantId', '==', user.uid),
      where('read', '==', false)
    );

    let recipientDocs = [];
    let tenantDocs = [];

    const updateCounts = () => {
      const notifications = mergeNotificationDocs(recipientDocs, tenantDocs).map((d) => ({
        id: d.id,
        ...d.data()
      }));
      setTenantNotificationCounts(countTenantNotificationBuckets(notifications));
    };

    const unsubRecipient = onSnapshot(recipientQuery, (snapshot) => {
      recipientDocs = snapshot.docs;
      updateCounts();
    }, () => {
      recipientDocs = [];
      updateCounts();
    });

    const unsubTenant = onSnapshot(tenantQuery, (snapshot) => {
      tenantDocs = snapshot.docs;
      updateCounts();
    }, () => {
      tenantDocs = [];
      updateCounts();
    });

    return () => {
      unsubRecipient();
      unsubTenant();
    };
  }, [user?.uid, userType]);

  // Real-time pending viewings count (tenants only — landlords use useLandlordBadges)
  useEffect(() => {
    if (!user?.uid || userType !== 'tenant') {
      setViewingsCountA(0);
      setViewingsCountB(0);
      return;
    }

    const tenantViewingsQuery = query(
      collection(db, 'viewings'),
      where('tenantUid', '==', user.uid),
      where('status', '==', 'confirmed')
    );
    const unsub = onSnapshot(tenantViewingsQuery, (snapshot) => {
      const now = new Date();
      const upcoming = snapshot.docs.filter(d => {
        const data = d.data();
        const startAt = data.startAt?.toDate ? data.startAt.toDate() : new Date(data.startAt || 0);
        return startAt > now;
      });
      setViewingsCountA(upcoming.length);
    }, () => setViewingsCountA(0));

    return () => unsub();
  }, [user?.uid, userType]);

  // Real-time pending contracts count (tenants only — landlords use useLandlordBadges)
  useEffect(() => {
    if (!user?.uid || userType !== 'tenant') {
      setTenantPendingContractsCount(0);
      return;
    }

    const q = query(
      collection(db, 'contracts'),
      where('tenantID', '==', user.uid),
      where('status', '==', 'sentToTenant')
    );
    const unsub = onSnapshot(q, (snapshot) => {
      setTenantPendingContractsCount(snapshot.docs.length);
    }, () => setTenantPendingContractsCount(0));

    return () => unsub();
  }, [user?.uid, userType]);

  useEffect(() => {
    if (!user?.uid || userType !== 'landlord' || landlordType === 'agency') {
      setNeedsProfileCompletion(false);
      return undefined;
    }

    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const completeName = !!(data.displayName || data.name || data.contactName || '').trim();
      const completePhone = !!(data.phone || '').trim();
      const completeAddress = !!(data.address || data.adres || '').trim();
      setNeedsProfileCompletion(!(completeName && completePhone && completeAddress));
    });

    return () => unsub();
  }, [user?.uid, userType, landlordType]);

  // All landlord notifications (read + unread) for bell dropdown history.
  // Only queries recipientUserId — the canonical field for "who should see this".
  useEffect(() => {
    if (!user?.uid || userType !== 'landlord' || landlordType === 'agency') {
      setAllLandlordNotifs([]);
      return;
    }

    const q = query(collection(db, 'notifications'), where('recipientUserId', '==', user.uid));

    const unsub = onSnapshot(q, (snap) => {
      const sorted = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds || 0) * 1000;
        const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds || 0) * 1000;
        return tb - ta;
      });
      setAllLandlordNotifs(sorted.slice(0, 50));
    }, () => setAllLandlordNotifs([]));

    return () => unsub();
  }, [user?.uid, userType, landlordType]);

  // Close bell dropdown on click outside
  useEffect(() => {
    if (!isBellOpen) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setIsBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isBellOpen]);

  // Close bell when navigating
  useEffect(() => { setIsBellOpen(false); }, [location.pathname]);

  const getNotifTime = (n) => {
    if (n.createdAt?.toDate) return n.createdAt.toDate().getTime();
    if (n.createdAt?.seconds) return n.createdAt.seconds * 1000;
    return 0;
  };

  const bellBadgeCount = allLandlordNotifs.filter((n) => getNotifTime(n) > bellSeenAt).length;

  const handleBellClick = () => {
    const wasOpen = isBellOpen;
    if (!wasOpen) {
      setBellHighlightBefore(bellSeenAt);
      setBellSeenAt(Date.now());
    }
    setIsBellOpen(!wasOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/welcome');
  };

  const handleProfileClick = () => {
    if (userType === 'tenant') {
      navigate('/account');
    } else if (userType === 'landlord') {
      if (landlordType === 'agency') {
        navigate('/landlord/agency/profile');
      } else {
        navigate('/landlord/profile');
      }
    }
  };

  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.pathname, location.hash, location.search]);

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!userMenuRef.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isUserMenuOpen]);

  const handleLogoClick = () => {
    if (userType === 'tenant') {
      navigate('/');
    } else if (userType === 'landlord') {
      if (landlordType === 'agency') {
        navigate('/landlord/agency/dashboard');
      } else {
        navigate('/landlord');
      }
    } else {
      navigate('/welcome');
    }
  };

  const getNavigationItems = () => {
    if (userType === 'tenant') {
      return [
        { path: '/', label: t('navigation.home'), icon: Home },
        { path: '/favorites', label: t('navigation.favorites'), icon: Star },
        { path: '/messages', label: t('navigation.messages'), icon: MessageCircle },
        { path: '/contracten', label: t('navigation.contracts', 'Contracten'), icon: FileText },
        { path: '/bezichtigingen', label: t('navigation.viewings', 'Bezichtigingen'), icon: Eye },
        { path: '/agenda', label: t('navigation.agenda', 'Agenda'), icon: CalendarDays },
        { path: '/mijn-pand', label: t('navigation.myProperty'), icon: Building },
      ];
    } else if (userType === 'landlord') {
      // Agency accounts have different navigation - Dashboard in blue, others normal
      if (landlordType === 'agency') {
        return [
          { path: '/landlord/agency/dashboard', label: 'Dashboard', icon: Home, isAgencyDashboard: true },
          { path: '/landlord/agency/clients', label: 'Clients', icon: User },
          { path: '/landlord/agency/properties', label: 'Panden', icon: Building },
          { path: '/landlord/agency/inbox', label: 'Inbox', icon: MessageCircle },
          { path: '/landlord/agency/viewings', label: 'Bezichtigingen', icon: Calendar },
          { path: '/landlord/agency/agenda', label: 'Agenda', icon: CalendarDays },
          { path: '/landlord/agency/candidates', label: 'Kandidaten', icon: FileText },
          { path: '/landlord/agency/billing', label: 'Abonnement', icon: FileText },
          { path: '/landlord/agency/profile', label: 'Profiel', icon: User }
        ];
      } else {
        // Private landlord navigation
        return [
          { path: '/landlord', label: t('navigation.dashboard'), icon: Home },
          { path: '/landlord/contracts', label: t('navigation.contracts'), icon: FileText },
          { path: '/landlord/messages', label: t('navigation.messages'), icon: MessageCircle },
          { path: '/landlord/verhuurde-panden', label: 'Verhuurde Panden', icon: Key },
          { path: '/landlord/viewings', label: t('navigation.viewings'), icon: Eye },
          { path: '/landlord/agenda', label: t('navigation.agenda'), icon: CalendarDays },
          { path: '/landlord/billing', label: 'Abonnement', icon: FileText },
          { path: '/landlord/profile', label: t('navigation.profile'), icon: User },
        ];
      }
    }
    return [];
  };

  // Map navigation paths to their unread badge counts
  const getBadgeCount = (path) => {
    // Private landlords: delegate entirely to the centralised hook
    if (userType === 'landlord' && landlordType !== 'agency') {
      return landlordBadges.getBadgeCount(path);
    }

    const messagesBadgeCount = landlordType === 'agency' ? unreadAgencyMessageCount : unreadMessageCount;
    if (['/messages', '/landlord/messages', '/landlord/agency/inbox', '/landlord/agency/messages'].includes(path)) {
      return messagesBadgeCount || 0;
    }
    if (userType === 'tenant') {
      if (path === '/contracten') return tenantNotificationCounts.contracts || 0;
      if (path === '/bezichtigingen') return tenantNotificationCounts.viewings || 0;
      if (path === '/messages') return unreadMessageCount || 0;
      if (path === '/mijn-pand') return tenantNotificationCounts.mijnPand || 0;
      if (path === '/agenda') return 0;
    }
    if (['/landlord/viewings', '/landlord/agency/viewings', '/agenda', '/bezichtigingen'].includes(path)) {
      return pendingViewingsCount || 0;
    }
    if (['/landlord/contracts', '/landlord/verhuurde-panden', '/contracten'].includes(path)) {
      return (userType === 'tenant' ? tenantPendingContractsCount : pendingContractsCount) || 0;
    }
    return 0;
  };

  const navigationItems = getNavigationItems().filter(
    (item) => !['/landlord/profile', '/landlord/agency/onboarding'].includes(item.path)
  );
  const landlordMenuItems = navigationItems.filter((item) => {
    if (landlordType === 'agency') {
      return item.path !== '/landlord/agency/profile';
    }
    return item.path !== '/landlord/profile';
  });
  const tenantMenuItems = navigationItems.filter((item) => item.path !== '/account');
  const dropdownMenuItems = userType === 'tenant'
    ? tenantMenuItems
    : landlordType === 'agency'
      ? []
      : landlordMenuItems;
  const isAgencyRoute = location.pathname.startsWith('/landlord/agency');
  const isPrivateLandlordRoute = location.pathname.startsWith('/landlord') && !isAgencyRoute;
  const isStandardRoute = !isAgencyRoute && !isPrivateLandlordRoute;
  const isBillingPage = location.pathname === '/landlord/billing' || location.pathname === '/landlord/agency/billing';
  const isRequiredBillingFlow = isBillingPage && new URLSearchParams(location.search).get('required') === '1';

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path === '/landlord/agency/inbox' && location.pathname.startsWith('/landlord/agency/messages')) return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };


  if (isRequiredBillingFlow) {
    return (
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
        <div className="h-[1px] w-full bg-[#5EABEB]/55" />
        <div className="container-professional">
          <div className="h-16 flex items-center justify-center">
            <button onClick={handleLogoClick} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <StudyStayLogo className="w-9 h-9" />
              <span className="text-2xl font-semibold text-slate-900">StudyStay</span>
            </button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`sticky top-0 z-50 overflow-visible ${
      isAgencyRoute
        ? 'bg-white/70 backdrop-blur-xl border-b border-slate-200/60'
        : isPrivateLandlordRoute
          ? 'bg-white/80 backdrop-blur-md border-b border-slate-200/80 shadow-sm'
          : 'bg-white/85 backdrop-blur-md border-b border-slate-200/70 shadow-sm'
    }`}>
      <div className={`h-[1px] w-full ${(isAgencyRoute || isPrivateLandlordRoute || isStandardRoute) ? 'bg-[#5EABEB]/55' : 'bg-transparent'}`} />
      <div className="container-professional max-w-full overflow-visible">
        <div className={`flex items-center justify-between gap-4 min-w-0 ${isAgencyRoute || isPrivateLandlordRoute ? 'h-14' : 'h-16'}`}>
          {/* Logo - Left aligned */}
          <div className="flex items-center flex-shrink-0 min-w-0">
            <button
              onClick={handleLogoClick}
              className={`flex items-center hover:opacity-80 transition-opacity whitespace-nowrap ${(isAgencyRoute || isPrivateLandlordRoute) ? 'gap-3' : 'space-x-2 sm:space-x-3'}`}
            >
              <StudyStayLogo className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0" />
              <span className="flex flex-col leading-tight">
                <span className={`${(isAgencyRoute || isPrivateLandlordRoute) ? 'text-gray-900 font-semibold' : 'text-gradient font-bold'} text-xl sm:text-2xl`}>StudyStay</span>
                {(isAgencyRoute || isPrivateLandlordRoute) && <span className="text-xs text-slate-500 font-medium tracking-wide">{isAgencyRoute ? 'Agency Portal' : 'Landlord Portal'}</span>}
              </span>
            </button>
          </div>

          {/* Desktop Navigation - Right side - Hidden when sidebar shell is active (agency + private landlord) */}
          {user && !isAgencyRoute && !isPrivateLandlordRoute && userType !== 'tenant' && userType !== 'landlord' && (
            <nav className="hidden xl:flex items-center gap-2 flex-wrap flex-shrink-0 min-w-0 ml-auto mr-6 max-w-full overflow-hidden">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const handleClick = () => {
                  if (item.hash) {
                    navigate(`${item.path}#${item.hash}`);
                  } else {
                    navigate(item.path);
                  }
                };
                // Special styling for agency dashboard button (blue), others normal
                const isAgencyButton = item.isAgencyDashboard;
                return (
                  <button
                    key={`${item.path}${item.hash || ''}`}
                    onClick={handleClick}
                    className={`relative flex items-center space-x-2 px-3 py-2 rounded-xl transition-all duration-200 min-w-0 focus:outline-none focus:ring-2 ${
                      isAgencyButton
                        ? 'bg-blue-600 text-white shadow-professional hover:bg-blue-700'
                        : isActive(item.path) || (item.hash && location.hash === `#${item.hash}`)
                          ? (isPrivateLandlordRoute || isStandardRoute
                              ? 'bg-[#5EABEB]/12 text-[#2B6FA6] border border-[#5EABEB]/25 shadow-sm ring-[#5EABEB]/25'
                              : 'bg-ios-blue text-white shadow-professional')
                          : (isPrivateLandlordRoute || isStandardRoute
                              ? 'text-slate-600 hover:text-[#2B6FA6] hover:bg-[#5EABEB]/8 ring-[#5EABEB]/25'
                              : 'text-gray-600 hover:text-ios-blue hover:bg-gray-50')
                    }`}
                    data-tutorial={
                      item.path === '/favorites' ? 'favorites-nav' :
                      item.path === '/messages' ? 'messages-nav' :
                      item.path === '/contracten' ? 'contracten-nav' :
                      item.path === '/bezichtigingen' ? 'bezichtigingen-nav' :
                      item.path === '/agenda' ? 'agenda-nav' :
                      item.path === '/account' ? 'account-nav' :
                      item.path === '/mijn-pand' ? 'mijn-pand-nav' :
                      item.path === '/landlord/contracts' ? 'contracts-nav' :
                      item.path === '/landlord/verhuurde-panden' ? 'rented-properties-nav' :
                      item.path === '/landlord/viewings' ? 'viewings-nav' :
                      item.path === '/landlord/agenda' ? 'agenda-nav' :
                      item.path === '/landlord/profile' ? 'profile-nav' :
                      null
                    }
                  >
                    <Icon className={`w-[18px] h-[18px] ${(isPrivateLandlordRoute || isStandardRoute) ? 'opacity-85' : ''}`} />
                    <span className="text-sm font-medium whitespace-nowrap truncate max-w-[120px]">{item.label}</span>
                    {getBadgeCount(item.path) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm">
                        {getBadgeCount(item.path) > 9 ? '9+' : getBadgeCount(item.path)}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          )}

          {/* User Menu - Only show if user is logged in */}
          {user && (
            <div ref={userMenuRef} className="relative flex items-center gap-3 flex-shrink-0 ml-auto">

              {/* Notification Bell — private landlord only */}
              {userType === 'landlord' && landlordType !== 'agency' && (
                <div ref={bellRef} className="relative">
                  <button
                    onClick={handleBellClick}
                    className="relative p-2 rounded-xl border border-slate-200 bg-white/95 hover:bg-slate-50 transition-all"
                    title="Meldingen"
                  >
                    <Bell className="w-5 h-5 text-slate-600" />
                    {bellBadgeCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[17px] h-[17px] flex items-center justify-center px-0.5 shadow-sm">
                        {bellBadgeCount > 9 ? '9+' : bellBadgeCount}
                      </span>
                    )}
                  </button>
                  {isBellOpen && (
                    <div className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-xl z-[80] flex flex-col">
                      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
                        <h3 className="text-sm font-bold text-slate-900">Meldingen</h3>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {allLandlordNotifs.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">Geen meldingen</p>
                          </div>
                        ) : (
                          <div className="py-1">
                            {allLandlordNotifs.map((notif) => {
                              const isNew = getNotifTime(notif) > bellHighlightBefore;
                              const timeStr = notif.createdAt?.toDate
                                ? notif.createdAt.toDate().toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                                : notif.createdAt?.seconds
                                  ? new Date(notif.createdAt.seconds * 1000).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                                  : '';
                              return (
                                <button
                                  key={notif.id}
                                  onClick={() => {
                                    setIsBellOpen(false);
                                    const t = (notif.type || '').toLowerCase();
                                    if (notif.actionUrl) {
                                      navigate(notif.actionUrl);
                                    } else if (t.includes('viewing') || t === 'viewing_request') {
                                      navigate(notif.propertyId ? `/landlord/viewings/${notif.propertyId}` : '/landlord/viewings');
                                    } else if (t.startsWith('termination_') || t === 'termination_notice') {
                                      navigate('/landlord/verhuurde-panden');
                                    } else if (t === 'payment_marked_paid' || t.includes('problem') || t === 'property_report') {
                                      navigate('/landlord/verhuurde-panden');
                                    } else if (t.includes('contract') || t.includes('amendment') || notif.contractId) {
                                      navigate(notif.contractId ? `/landlord/contracts/${notif.contractId}` : '/landlord/contracts');
                                    } else if (notif.propertyId) {
                                      navigate('/landlord/verhuurde-panden');
                                    } else {
                                      navigate('/landlord');
                                    }
                                  }}
                                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${isNew ? 'bg-blue-50/60' : ''}`}
                                >
                                  <div className="flex items-start gap-3">
                                    {isNew && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                                    {!isNew && <span className="mt-1.5 w-2 h-2 rounded-full bg-transparent shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm ${isNew ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                                        {notif.title || notif.type?.replace(/_/g, ' ') || 'Melding'}
                                      </p>
                                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message || ''}</p>
                                      {timeStr && <p className="text-[11px] text-slate-400 mt-1">{timeStr}</p>}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => {
                  if (userType === 'landlord' || userType === 'tenant') {
                    setIsUserMenuOpen((prev) => !prev);
                    return;
                  }
                  handleProfileClick();
                }}
                data-tutorial={userType === 'tenant' ? 'account-nav' : undefined}
                className={`flex items-center gap-2 transition-all duration-200 ${
                  needsProfileCompletion
                    ? 'ring-2 ring-amber-300'
                    : ''
                } ${
                  isAgencyRoute
                    ? 'px-3 py-1.5 rounded-full hover:bg-slate-100'
                    : isPrivateLandlordRoute || isStandardRoute
                      ? 'px-3 py-1.5 rounded-full border border-[color:var(--brand-soft)] bg-white/95 backdrop-blur-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/30'
                      : 'px-3 py-2 rounded-lg hover:bg-gray-50'
                }`}
              >
                <div className="relative">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-base ${
                    isAgencyRoute
                      ? 'bg-white ring-2 ring-[#5EABEB]/20 text-slate-700'
                      : isPrivateLandlordRoute || isStandardRoute
                        ? 'bg-white ring-2 ring-[#5EABEB]/30 text-slate-700'
                        : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white'
                  }`}>
                    {user?.displayName ? user.displayName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  {(() => {
                    const total = userType === 'tenant'
                      ? (tenantNotificationCounts.contracts || 0)
                        + (tenantNotificationCounts.viewings || 0)
                        + (tenantNotificationCounts.mijnPand || 0)
                        + (unreadMessageCount || 0)
                      : userType === 'landlord' && landlordType !== 'agency'
                        ? (landlordBadges.pendingViewingsCount || 0) + (landlordBadges.pendingContractsCount || 0) + (landlordBadges.amendmentActionCount || 0) + (landlordBadges.unreadMessageCount || 0)
                        : 0;
                    return total > 0 ? (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[17px] h-[17px] flex items-center justify-center px-0.5 shadow-sm">
                        {total > 9 ? '9+' : total}
                      </span>
                    ) : null;
                  })()}
                </div>
                <span className="hidden lg:block text-sm font-medium text-gray-900">{t('navigation.profile', 'Profiel')}</span>
                {needsProfileCompletion && (
                  <span className="hidden lg:block text-[11px] font-semibold text-amber-700">Aanvullen</span>
                )}
                {(userType === 'landlord' || userType === 'tenant') && <ChevronDown className={`hidden lg:block w-4 h-4 text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />}
                {needsProfileCompletion && <span className="w-2.5 h-2.5 rounded-full bg-amber-500 ml-1" />}
              </button>
              {(userType === 'landlord' || userType === 'tenant') && isUserMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-xl p-2 z-[80]">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-900">{user.displayName || user.email || 'Gebruiker'}</p>
                    <p className="text-xs text-slate-500">
                      {userType === 'tenant'
                        ? 'Huurder'
                        : landlordType === 'agency'
                          ? 'Agency verhuurder'
                          : 'Particuliere verhuurder'}
                    </p>
                  </div>
                  <div className="py-2 space-y-1 max-h-[70vh] overflow-y-auto">
                    {dropdownMenuItems.map((item) => {
                      const Icon = item.icon;
                      const badge = getBadgeCount(item.path);
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={() => {
                            navigate(item.path);
                            setIsUserMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                            active
                              ? 'bg-[#5EABEB]/12 text-[#2B6FA6] border border-[#5EABEB]/20'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${active ? 'text-[#2B6FA6]' : 'text-slate-400'}`} />
                          <span className="flex-1 text-sm font-medium">{item.label}</span>
                          {badge > 0 && (
                            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                              {badge > 9 ? '9+' : badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {dropdownMenuItems.length > 0 && <div className="border-t border-slate-100 my-1" />}
                    <button
                      onClick={() => {
                        handleProfileClick();
                        setIsUserMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                        needsProfileCompletion
                          ? 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <User className={`w-4 h-4 ${needsProfileCompletion ? 'text-amber-600' : 'text-slate-400'}`} />
                      <span className="flex-1 text-sm font-medium">{t('navigation.profile', 'Profiel')}</span>
                      {needsProfileCompletion && <span className="text-[11px] font-semibold">Aanvullen</span>}
                    </button>
                    {landlordType !== 'agency' && (
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('common.logout')}</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
              {userType !== 'landlord' && userType !== 'tenant' && (
                <button
                  onClick={handleLogout}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    isPrivateLandlordRoute || isStandardRoute
                      ? 'text-slate-400 hover:text-slate-600 hover:bg-[#5EABEB]/10 focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/25'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  title={t('common.logout')}
                >
                  <LogOut className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Mobile Menu Button - Hidden when sidebar shell is active */}
          {user && !isAgencyRoute && !isPrivateLandlordRoute && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 ml-2"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>

        {/* Mobile Navigation - Hidden when sidebar shell is active */}
          {user && isMobileMenuOpen && !isAgencyRoute && !isPrivateLandlordRoute && (
          <div className="xl:hidden border-t border-gray-200 bg-white">
            <nav className="py-2 space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const handleClick = () => {
                  if (item.hash) {
                    navigate(`${item.path}#${item.hash}`);
                  } else {
                    navigate(item.path);
                  }
                  setIsMobileMenuOpen(false);
                };
                // Special styling for agency dashboard button (blue), others normal
                const isAgencyButton = item.isAgencyDashboard;
                return (
                  <button
                    key={`${item.path}${item.hash || ''}`}
                    onClick={handleClick}
                    className={`flex items-center space-x-3 w-full px-4 py-2.5 rounded-lg transition-all duration-200 ${
                      isAgencyButton
                        ? 'bg-blue-600 text-white shadow-professional hover:bg-blue-700'
                        : isActive(item.path) || (item.hash && location.hash === `#${item.hash}`)
                          ? ((isPrivateLandlordRoute || isStandardRoute) ? 'bg-[#5EABEB]/12 text-[#2B6FA6] border border-[#5EABEB]/25 shadow-sm' : 'bg-ios-blue text-white shadow-professional')
                          : ((isPrivateLandlordRoute || isStandardRoute) ? 'text-slate-600 hover:text-[#2B6FA6] hover:bg-[#5EABEB]/8' : 'text-gray-600 hover:text-ios-blue hover:bg-gray-50')
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {getBadgeCount(item.path) > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {getBadgeCount(item.path) > 9 ? '9+' : getBadgeCount(item.path)}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
